/**
 * Mapping between the 9-state phrase FSM and the 5 visual segments
 * surfaced by the journey shell (`MiniSteps`).
 *
 *   Step 0 → S0        Présentation
 *   Step 1 → S1a ∪ S1b Main gauche (prise, puis pratique)
 *   Step 2 → S2a ∪ S2b Main droite
 *   Step 3 → S3 ∪ S4   Mains ensemble (avec ou sans rythme)
 *   Step 4 → S5        Enchaînement
 *
 * Two compressions, for two different reasons.
 *
 * `S3` / `S4` share a segment because from the player's perspective they
 * are one step ("jouer les deux mains"), and the sub-graduation isn't
 * worth surfacing.
 *
 * `S1a` / `S1b` share one because **la prise is not a milestone**: it is
 * the hand settling before its sequence, the first gesture of practising
 * that hand, not a thing to be learnt on its own. Giving it a segment also
 * made the stepper flicker — the step runs on about half the hands and
 * skips on the rest, so a slot appeared and vanished from phrase to
 * phrase, which reads as a bug. The count is now constant. The FSM keeps
 * both states, for persistence and for the revisit affordance.
 *
 * For mono-hand phrases the FSM skips the absent hand entirely, plus `S3`.
 * Those indices do not appear in `applicableStepIndices`, and the visual
 * renders that list rather than the table: a unit with three steps draws
 * three segments and counts to three. See `stepPosition`.
 *
 * Mirrors the reachability rules of `PhraseFSM.advanceFromS0` and
 * `fastForwardTo`, kept in sync by the mono/bi-hand tests.
 */

import type { Phrase, PhraseState } from './types';

export const TOTAL_STEPS = 5;

/**
 * Visual step index a `PhraseState` belongs to.
 *
 * `DONE` is reported on the last step (S5, the chain step) so a fully
 * validated phrase keeps the progress bar full instead of snapping back
 * to "presentation".
 */
export function phraseStateToStepIndex(state: PhraseState): number {
  switch (state) {
    case 'S0':
      return 0;
    case 'S1a':
    case 'S1b':
      return 1;
    case 'S2a':
    case 'S2b':
      return 2;
    case 'S3':
    case 'S4':
      return 3;
    case 'S5':
    case 'DONE':
      return 4;
  }
}

/**
 * Inverse of `phraseStateToStepIndex`. Picks the *entry* state of the
 * step, so jumping back to a hand lands on its prise rather than mid-way
 * through its practice, and step 3 lands in `S3` rather than `S4`. The
 * FSM's `coerceToLegalState` folds the prise onto the practice when the
 * hand has none, so this never lands on a state that does not exist.
 *
 * Returns `null` when the index is out of range.
 */
export function stepIndexToPhraseState(index: number): PhraseState | null {
  switch (index) {
    case 0:
      return 'S0';
    case 1:
      return 'S1a';
    case 2:
      return 'S2a';
    case 3:
      return 'S3';
    case 4:
      return 'S5';
    default:
      return null;
  }
}

/**
 * Step indices that are pedagogically applicable to a given phrase.
 *
 * Mirrors `PhraseFSM`'s skipping rules: a phrase with no notes on a hand
 * drops that hand's segment, and a mono-hand phrase also drops `S3`
 * (handled by collapsing step 3, applicable only when both hands are
 * present).
 *
 * Whether the hand's prise runs does **not** appear here: it shares its
 * segment with the practice, so a hand that plays always has its segment
 * whether or not it settles anywhere.
 *
 * Always includes `0` (S0 presentation). Includes `4` (S5 chain) unless
 * `chains` says the unit has nothing before it to be joined to, which is
 * the case of the first unit of a piece — see `PhraseFSM`'s constructor.
 */
export function applicableStepIndices(
  phrase: Phrase,
  chains = true,
): readonly number[] {
  const hasLeft = phrase.notesByHand.left.length > 0;
  const hasRight = phrase.notesByHand.right.length > 0;
  const out: number[] = [0];

  if (hasLeft) {
    out.push(1);
  }

  if (hasRight) {
    out.push(2);
  }

  if (hasLeft || hasRight) {
    out.push(3);

    if (chains) {
      out.push(4);
    }
  }

  return out;
}

/**
 * Where a step sits among the ones this phrase actually has, 1-based.
 *
 * The absolute index is a slot in a five-slot table, and three of those slots
 * are conditional. On a right-hand-only unit the first screen the player ever
 * sees announced itself as « 3/5 » — two steps that do not exist, counted
 * against them before they have played a note, and no fourth or fifth coming
 * either. The table is an implementation detail of the FSM; what the player
 * is owed is how far along *their* unit they are.
 *
 * A step that is not in the list is reported at the rank it would hold —
 * how many applicable steps it is at or past. `DONE` maps to the chaining
 * step, which the first unit of a piece does not have; without this it
 * announced « 5/3 ».
 */
export function stepPosition(
  applicable: readonly number[],
  index: number,
): number {
  const at = applicable.indexOf(index);

  if (at >= 0) {
    return at + 1;
  }

  let rank = 0;

  for (const step of applicable) {
    if (step <= index) {
      rank += 1;
    }
  }

  return Math.max(1, rank);
}

/**
 * Step indices the player can revisit at the current snapshot:
 *
 *   - the segment must be applicable to the phrase (mono-hand filter),
 *   - and it must have already been reached (its state index is `<=`
 *     the FSM's `maxState`).
 *
 * The current step is *included*, the caller (`MiniSteps`) marks it
 * non-interactive on its own based on `active`.
 */
export function reachableStepIndices(
  phrase: Phrase,
  maxState: PhraseState,
  chains = true,
): readonly number[] {
  const applicable = applicableStepIndices(phrase, chains);
  const maxIdx = phraseStateToStepIndex(maxState);

  return applicable.filter((idx) => idx <= maxIdx);
}
